import { ALL_MODULES, type ModuleKey } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface ModulePermissionTreeProps {
  value: ModuleKey[];
  onChange: (next: ModuleKey[]) => void;
  error?: string | null;
  className?: string;
}

/**
 * 权限树(ADP-167,Xnurta 交互:全选 + 模块勾选)。AdPraxis 裁剪为模块级
 * 单层树;层级结构保留列表形态以便未来加子项。
 */
export function ModulePermissionTree({
  value,
  onChange,
  error,
  className,
}: ModulePermissionTreeProps) {
  const { t } = useTranslation("account");
  const allChecked = ALL_MODULES.every((key) => value.includes(key));

  function toggle(key: ModuleKey, checked: boolean) {
    onChange(checked ? [...value, key] : value.filter((existing) => existing !== key));
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className={cn("rounded-md border p-3", error ? "border-destructive" : "border-input")}>
        <Label className="flex items-center gap-2 pb-2 font-medium">
          <Checkbox
            checked={allChecked}
            indeterminate={!allChecked && value.length > 0}
            onCheckedChange={(checked) => onChange(checked ? [...ALL_MODULES] : [])}
            aria-label={t("drawer.selectAll")}
          />
          <span className="text-sm">{t("drawer.selectAll")}</span>
        </Label>
        <ul className="flex flex-col gap-2 pl-6">
          {ALL_MODULES.map((key) => (
            <li key={key}>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={value.includes(key)}
                  onCheckedChange={(checked) => toggle(key, checked === true)}
                  aria-label={t(`modules.${key}`)}
                />
                <span className="text-sm">{t(`modules.${key}`)}</span>
              </Label>
            </li>
          ))}
        </ul>
      </div>
      {error ? <p className="text-xs text-destructive-foreground">{error}</p> : null}
    </div>
  );
}
